import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useInView, useMotionValue, useReducedMotion, useSpring, type Variants } from "motion/react";
import { cn } from "../lib/cn";

export { AnimatePresence, motion };

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

export function Stagger({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div className={className} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: delay } } }} initial="hidden" animate="show">
      {children}
    </motion.div>
  );
}

export function FadeUp({ children, className, delay = 0, as = "div" }: { children: ReactNode; className?: string; delay?: number; as?: "div" | "section" | "li" | "article" }) {
  const Comp = motion[as];
  return (
    <Comp className={className} variants={fadeUp} initial="hidden" animate="show" transition={{ delay }}>
      {children}
    </Comp>
  );
}

export function Reveal({ children, className, once = true }: { children: ReactNode; className?: string; once?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once, margin: "-10% 0px" });
  return (
    <motion.div ref={ref} className={className} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : undefined} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
}

/** Route-level transition wrapper. Use with a `key` that changes per route. */
export function PageTransition({ children, className, routeKey }: { children: ReactNode; className?: string; routeKey: string }) {
  const reduce = useReducedMotion();
  // popLayout lets the incoming route mount immediately (the outgoing one fades out of the layout flow), so a paused
  // animation frame in a background tab can never leave the page blank.
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={routeKey}
        className={className}
        initial={reduce ? false : { opacity: 0, y: 10, filter: "blur(2px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={reduce ? undefined : { opacity: 0, y: -6, filter: "blur(2px)" }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Smoothly animates numbers (counters, KPIs). */
export function AnimatedNumber({ value, format, className, duration = 1.2 }: { value: number; format?: (n: number) => string; className?: string; duration?: number }) {
  const reduce = useReducedMotion();
  const mv = useMotionValue(reduce ? value : 0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });
  const [display, setDisplay] = useState(() => (format ?? defaultFormat)(reduce ? value : 0));

  useEffect(() => {
    mv.set(value);
  }, [value, mv]);

  useEffect(() => {
    // Re-format the settled value too, so a locale switch (new `format`) is reflected without the number moving.
    setDisplay((format ?? defaultFormat)(spring.get()));
    const unsub = spring.on("change", (v) => setDisplay((format ?? defaultFormat)(v)));
    return () => unsub();
  }, [spring, format]);

  return <span className={cn("tabular", className)}>{display}</span>;
}

function defaultFormat(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : Math.round(n).toLocaleString();
}

/** Typewriter-ish reveal used for streaming assistant tokens. */
export function StreamingCaret({ active }: { active: boolean }) {
  if (!active) return null;
  return <span className="inline-block w-0.5 h-4 bg-aegold-600 ms-0.5 align-middle animate-caret" aria-hidden />;
}
